import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { matchSorter } from 'match-sorter';
import { useTheme } from 'emotion-theming';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import Avatar from '../../../shared/_TBComponents/Avatar/Avatar';
import Button from '../../../shared/_TBComponents/Button';
import Icon from '../../../shared/_TBComponents/Icon';
import Text from '../../../shared/_TBComponents/Text';
import hexToRgba from '../../../shared/utils/hexToRgba';
import AthleteResults from './presentational/AthleteResults';
import AthleteAssignments from './presentational/AthleteAssignments';
import AthletePreview from './presentational/AthletePreview';
import AddOptionsModal from './presentational/AddOptionsModal';
import AddAthleteModal from './presentational/AddAthleteModal';
import EmptyStateArrow from './presentational/EmptyStateArrow';
import NavigatorSelectorPanel from './presentational/NavigatorSelectorPanel';
import { fetchUser, fetchAthletes } from '../ducks/navigatorActions';

const Container = styled.div`
  display: flex;
  height: 100%;
`;

const Section = styled.div`
  padding: 16px 8px;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;

  &:last-child {
    flex: 1;
    padding: 16px 8px;
    border-right: none;
    overflow-y: auto;
    box-shadow: inset 8px 0 12px -12px rgba(0,0,0,0.18);

  &::-webkit-scrollbar {
    width: 8px;
    opacity: 0;
    transition: opacity 0.3s;
  }
  &:hover::-webkit-scrollbar {
    opacity: 1;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #999;
  }

  scrollbar-width: thin;
  scrollbar-color: #ccc #f1f1f1;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px;
`;

const TopRow = styled.div`
  display: flex;
  gap: 8px;
  height: 45%;
  margin-top: 8px;

  @media screen and (max-width: 1200px) {
    flex-direction: column;
    height: auto;
  }
`;

const BottomRow = styled.div`
  display: flex;
  flex: 1;
  margin-top: 8px;
`;

const AthleteItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  margin-bottom: 8px;
  gap: 8px;
  border: ${(props) => (props.isSelected
    ? `2px solid ${props.bgColor}`
    : '1px solid #EEE')};
  background: ${(props) => (props.isSelected ? props.selectedBg : 'transparent')};
  box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 4px;

  &:hover {
    opacity: 0.7;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 8px;
  height: 100%;
  width: 50%;
  padding: 0 8px;
`;

const SelectEmptyState = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  height: 100%;
  max-width: 400px;
  padding: 60px 8px 0;
`;

const EmptyStateText = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: 8px;
`;

const MobilePage = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const MobileHeader = styled.div`
  padding: 12px 16px 4px;
  flex-shrink: 0;
`;

const MobileBackLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #77A7FF;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MobileSectionTitle = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #444;
  padding: 6px 0 4px;
`;

const MobileDetailContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  min-height: 0;

  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
  &::-webkit-scrollbar-thumb { background: #ccc; border-radius: 4px; }
  &::-webkit-scrollbar-thumb:hover { background: #999; }
  scrollbar-width: thin;
  scrollbar-color: #ccc #f1f1f1;
`;

const AthleteNavigator = ({
  athleteId, manageUsers, isMobile, onEditAthlete,
}) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const accountPrimaryColor = currentUser?.id
    ? (theme?.colorPrimary || currentUser?.userSkin?.primary || '#FF6600')
    : undefined;
  const selectedAthleteBg = accountPrimaryColor ? hexToRgba(accountPrimaryColor, 0.05) : 'transparent';

  const athletes = useSelector((state) => state.navigator.data.athletes);
  const athletesError = useSelector((state) => state.navigator.data.athletesError);
  const isLoading = useSelector((state) => state.navigator.ui.isLoadingAthletes);

  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [isAddOptionsModalOpen, setIsAddOptionsModalOpen] = useState(false);
  const [isAddAthleteModalOpen, setIsAddAthleteModalOpen] = useState(false);

  const scrollContainerRef = React.useRef(null);
  const hasScrolledToTarget = React.useRef(false);

  const queryParams = new URLSearchParams(location.search);
  const targetDate = queryParams.get('targetDate');
  // Clear target from URL after scroll completes to prevent unwanted behaviour
  useEffect(() => {
    if (targetDate && athleteId && hasScrolledToTarget.current) {
      const timer = setTimeout(() => {
        history.replace(`/dashboard/athletes/${athleteId}`);
      }, 2000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [targetDate, athleteId, history, hasScrolledToTarget.current]);

  useEffect(() => {
    if (targetDate) {
      hasScrolledToTarget.current = true;
    }
  }, [targetDate]);

  // Reset scroll when athlete selection changes
  useEffect(() => {
    if (scrollContainerRef.current && !targetDate && !hasScrolledToTarget.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [athleteId]);

  // Keep selectedAthlete in sync when athlete data changes (name, pic, etc.)
  useEffect(() => {
    if (athleteId && athletes && athletes.length > 0) {
      const found = athletes.find((a) => String(a.id) === String(athleteId));
      setSelectedAthlete(found || null);
    } else if (!athleteId) {
      setSelectedAthlete(null);
    }
  }, [athleteId, athletes]);

  // Preload athlete details for edit modal — only when the selected athlete changes
  useEffect(() => {
    if (athleteId && currentUser?.accountCode) {
      dispatch(fetchUser(currentUser.accountCode, Number(athleteId)));
    }
  }, [athleteId, currentUser?.accountCode, dispatch]);

  const addAthleteOptions = [
    {
      key: 'easy-join',
      title: 'Easy-Join',
      description: 'Generate a shareable link and allow athletes to create their own profile.',
      icon: 'Link',
      onClick: () => history.push('/easy-join', { backPath: '/dashboard', backLabel: 'Back to Home' }),
    },
    {
      key: 'import-bulk',
      title: 'Import in Bulk',
      description: 'Create multiple athlete profiles using a CSV file.',
      icon: 'Users',
      onClick: () => {
        setIsAddOptionsModalOpen(false);
        history.push('/dashboard/manage-athletes', { openUploadModal: true });
      },
    },
    {
      key: 'one-at-a-time',
      title: 'Individual Athlete',
      description: 'Create a single athlete profile.',
      icon: 'add-user',
      onClick: () => {
        setIsAddOptionsModalOpen(false);
        setIsAddAthleteModalOpen(true);
      },
    },
  ];

  const selectorButtons = [
    ...(manageUsers ? [
      {
        name: 'Add',
        icon: 'add-user',
        iconSize: 20,
        iconColor: '#9E9E9E',
        onClick: () => setIsAddOptionsModalOpen(true),
      },
      {
        name: 'Manage',
        onClick: () => history.push('/dashboard/manage-athletes'),
      },
    ] : []),
  ];

  const isInvalidAthleteId = !!athleteId
    && !isLoading
    && athletes?.length > 0
    && !athletes.some((a) => String(a.id) === String(athleteId));

  let mainContent;
  if (selectedAthlete) {
    mainContent = (
      <>
        <HeaderContainer>
          <Avatar
            avatar={selectedAthlete.pic}
            name={`${selectedAthlete.first} ${selectedAthlete.last}`}
            size={40}
          />
          <Text styledTextProps={{ fontSize: '24px', fontWeight: 700 }}>
            {`${selectedAthlete.first} ${selectedAthlete.last}`}
          </Text>
          {manageUsers && (
          <Button
            onClick={() => onEditAthlete()}
            styledWrapperProps={{ paddingLeft: '8px', marginTop: '4px' }}
            name='Edit'
            fontSize='16px'
            styledTextProps={{ fontWeight: 700, letterSpacing: '0.5px' }}
            txtColor='#77A7FF'
            bgColor='transparent'
            noBorder
          />
          )}
        </HeaderContainer>
        <TopRow>
          <AthleteAssignments
            selectedAthlete={selectedAthlete}
            manageUsers={manageUsers}
            onEditAthlete={onEditAthlete}
          />
          <AthletePreview selectedAthlete={selectedAthlete} />
        </TopRow>
        <BottomRow>
          <AthleteResults
            selectedAthlete={selectedAthlete}
            targetDate={targetDate}
            scrollContainerRef={scrollContainerRef}
          />
        </BottomRow>
      </>
    );
  } else if (athletesError && !isLoading) {
    mainContent = (
      <EmptyState>
        <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
          {athletesError}
        </Text>
      </EmptyState>
    );
  } else if (isInvalidAthleteId) {
    mainContent = (
      <EmptyState>
        <Text styledTextProps={{ fontSize: '18px', fontWeight: 700 }}>
          Athlete could not be found
        </Text>
        <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
          The athlete ID in the URL does not exist in this account.
        </Text>
      </EmptyState>
    );
  } else if (!athleteId) {
    mainContent = (
      <SelectEmptyState>
        <EmptyStateArrow color={accountPrimaryColor || 'transparent'} />
        <EmptyStateText>
          <Text styledTextProps={{ fontSize: '18px', fontWeight: 700 }}>
            Select an athlete
          </Text>
          <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
            Select an athlete to see their assignments and workout results
          </Text>
        </EmptyStateText>
      </SelectEmptyState>
    );
  }
  const renderAthleteList = ({ selectedRef, searchFilter }) => {
    const filtered = searchFilter && athletes
      ? matchSorter(athletes, searchFilter, { keys: ['first', 'last', (a) => `${a.first} ${a.last}`] })
      : athletes;

    if (athletesError && !searchFilter) {
      return (
        <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
          {athletesError}
        </Text>
      );
    }

    if (!filtered || filtered.length === 0) {
      return (
        <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
          {searchFilter ? 'No athletes match your search' : 'No athletes found'}
        </Text>
      );
    }

    const sortedFiltered = [...filtered].sort((a, b) => (a.last || '').localeCompare(b.last || ''));

    return sortedFiltered.map((athlete) => (
      <AthleteItem
        key={athlete.id}
        isSelected={selectedAthlete?.id === athlete.id}
        bgColor={accountPrimaryColor}
        selectedBg={selectedAthleteBg}
        ref={selectedAthlete?.id === athlete.id ? selectedRef : null}
        onClick={() => {
          hasScrolledToTarget.current = false;
          history.push(`/dashboard/athletes/${athlete.id}`);
        }}
      >
        <Avatar avatar={athlete.pic} size={30} name={`${athlete.first} ${athlete.last}`} />
        <Text
          styledTextProps={{
            fontSize: '16px',
            fontWeight: selectedAthlete?.id === athlete.id ? 700 : 400,
          }}
        >
          {athlete.first}
          {' '}
          {athlete.last}
        </Text>
      </AthleteItem>
    ));
  };

  const mobileModals = (
    <>
      <AddOptionsModal
        isOpen={isAddOptionsModalOpen}
        onClose={() => setIsAddOptionsModalOpen(false)}
        options={addAthleteOptions}
      />
      <AddAthleteModal
        isOpen={isAddAthleteModalOpen}
        onClose={() => {
          setIsAddAthleteModalOpen(false);
          dispatch(fetchAthletes(currentUser?.accountCode));
        }}
        onCreated={(newUser) => history.push(`/dashboard/athletes/${newUser.id}`)}
      />
    </>
  );

  if (isMobile) {
    if (athleteId) {
      return (
        <>
          {mobileModals}
          <MobilePage>
            <MobileHeader>
              <MobileBackLink
                type='button'
                onClick={() => history.push('/dashboard/athletes')}
              >
                <Icon icon='left-arrow' size={12} color='#77A7FF' />
                Back to Athletes
              </MobileBackLink>
            </MobileHeader>
            <MobileDetailContainer ref={scrollContainerRef}>
              {mainContent}
            </MobileDetailContainer>
          </MobilePage>
        </>
      );
    }
    return (
      <>
        {mobileModals}
        <MobilePage>
          <MobileHeader>
            <MobileBackLink
              type='button'
              onClick={() => history.push('/dashboard')}
            >
              <Icon icon='left-arrow' size={12} color='#77A7FF' />
              Back to Home
            </MobileBackLink>
            <MobileSectionTitle>Athletes</MobileSectionTitle>
          </MobileHeader>
          <NavigatorSelectorPanel
            fullWidth
            buttons={selectorButtons}
            searchPlaceholder='Search athletes'
            isLoading={isLoading}
            selectedId={selectedAthlete?.id}
            restoreSelectedScrollRef={false}
            renderList={renderAthleteList}
          />
        </MobilePage>
      </>
    );
  }

  return (
    <Container>
      <AddOptionsModal
        isOpen={isAddOptionsModalOpen}
        onClose={() => setIsAddOptionsModalOpen(false)}
        options={addAthleteOptions}
      />
      <AddAthleteModal
        isOpen={isAddAthleteModalOpen}
        onClose={() => {
          setIsAddAthleteModalOpen(false);
          dispatch(fetchAthletes(currentUser?.accountCode));
        }}
        onCreated={(newUser) => history.push(`/dashboard/athletes/${newUser.id}`)}
      />
      <NavigatorSelectorPanel
        width={285}
        buttons={selectorButtons}
        searchPlaceholder='Search athletes'
        isLoading={isLoading}
        selectedId={selectedAthlete?.id}
        restoreSelectedScrollRef={Boolean(athleteId)}
        renderList={renderAthleteList}
      />
      <Section ref={scrollContainerRef}>
        {mainContent}
      </Section>
    </Container>
  );
};

AthleteNavigator.propTypes = {
  athleteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onEditAthlete: PropTypes.func.isRequired,
  manageUsers: PropTypes.bool,
  isMobile: PropTypes.bool,
};

AthleteNavigator.defaultProps = {
  athleteId: undefined,
  manageUsers: false,
  isMobile: false,
};

export default AthleteNavigator;
